<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useApi } from '@/composables/useApi'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'
import DncGroupDialog from '@/views/apps/dnc/DncGroupDialog.vue'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'DncGroup',
  },
})

// Composables
const { t } = useI18n()
const { showToast } = useToast()

// Reactive data
// const dncStats = ref({
//   totalDncLists: 0,
//   totalDncGroups: 0,
//   activeDncLists: 0,
//   inactiveDncLists: 0,
//   topDncGroups: [] as any[],
// })

const selectedGroup = ref(null)
const showDialog = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')

// Search and filters
const searchQuery = ref('')
const isActiveFilter = ref()

// Table configuration
const headers = computed(() => [
  { title: t('dnc.groupName'), key: 'name', sortable: true },
  { title: t('dnc.description'), key: 'description', sortable: false },
  { title: t('dnc.dncLists'), key: 'dncListsCount', sortable: true },
  { title: t('dnc.isActive'), key: 'isActive', sortable: true },
  { title: t('dnc.createdAt'), key: 'createdAt', sortable: true },
  { title: t('dnc.actions'), key: 'actions', sortable: false, width: '150px' },
])

// Table pagination
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// totalItems is now computed from useApi data

// Table options handler
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Fetching DNC groups with useApi
const { data: dncGroupsData, execute: fetchDncGroups, isFetching: loading } = useApi<any>(createUrl('/dnc/groups', {
  query: {
    page,
    itemsPerPage,
    q: searchQuery,
    isActive: isActiveFilter,
    sortBy,
    orderBy,
  },
}))

// Refresh function for external calls
const refreshDncGroups = () => {
  fetchDncGroups()
  fetchDncStats()
}

// Computed properties for data
const dncGroups = computed(() => {
  const groups = dncGroupsData.value?.data || []

  return groups.map((group: any) => ({
    ...group,
    dncListsCount: group._count?.dncLists || 0,
  }))
})

const totalItems = computed(() => dncGroupsData.value?.total || 0)

// 👉 Fetching DNC stats with useApi
const { data: dncStatsData, execute: fetchDncStats } = useApi<any>('/dnc/stats')

const dncStats = computed(() => dncStatsData.value || {})

const openCreateDialog = () => {
  selectedGroup.value = null
  dialogMode.value = 'create'
  showDialog.value = true
}

const openEditDialog = (group: any) => {
  selectedGroup.value = { ...group }
  dialogMode.value = 'edit'
  showDialog.value = true
}

const handleDialogClose = () => {
  showDialog.value = false
  selectedGroup.value = null
  fetchDncGroups()
}

const toggleActiveStatus = async (group: any) => {
  try {
    await $api(`/dnc/groups/${group.id}`, {
      method: 'PATCH',
      body: {
        isActive: !group.isActive,
      },
    })

    showToast({
      type: 'success',
      message: t('dnc.success'),
    })
    fetchDncGroups()
    fetchDncStats()
  }
  catch (error) {
    showToast({
      type: 'error',
      message: t('dnc.error'),
    })
  }
}

// Lifecycle
onMounted(() => {
  fetchDncGroups()
  fetchDncStats()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('dnc.dncManagement') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('dnc.manageDncGroups') }}
      </p>
    </div>
    <!-- Stats Cards -->
    <VRow class="mb-6">
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText>
            <div class="d-flex align-center">
              <VAvatar
                color="primary"
                variant="tonal"
                size="44"
              >
                <VIcon icon="tabler-phone-off" />
              </VAvatar>
              <div class="ms-3">
                <p class="text-sm mb-0">
                  {{ t('dnc.totalDncLists') }}
                </p>
                <h6 class="text-h6 mb-0">
                  {{ dncStats.totalDncLists?.toLocaleString() || 0 }}
                </h6>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText>
            <div class="d-flex align-center">
              <VAvatar
                color="info"
                variant="tonal"
                size="44"
              >
                <VIcon icon="tabler-folder" />
              </VAvatar>
              <div class="ms-3">
                <p class="text-sm mb-0">
                  {{ t('dnc.totalDncGroups') }}
                </p>
                <h6 class="text-h6 mb-0">
                  {{ dncStats.totalDncGroups || 0 }}
                </h6>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText>
            <div class="d-flex align-center">
              <VAvatar
                color="success"
                variant="tonal"
                size="44"
              >
                <VIcon icon="tabler-check" />
              </VAvatar>
              <div class="ms-3">
                <p class="text-sm mb-0">
                  {{ t('dnc.activeDncLists') }}
                </p>
                <h6 class="text-h6 mb-0">
                  {{ dncStats.activeDncLists?.toLocaleString() || 0 }}
                </h6>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText>
            <div class="d-flex align-center">
              <VAvatar
                color="warning"
                variant="tonal"
                size="44"
              >
                <VIcon icon="tabler-x" />
              </VAvatar>
              <div class="ms-3">
                <p class="text-sm mb-0">
                  {{ t('dnc.inactiveDncLists') }}
                </p>
                <h6 class="text-h6 mb-0">
                  {{ dncStats.inactiveDncLists?.toLocaleString() || 0 }}
                </h6>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Main Content -->
    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select IsActive -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="isActiveFilter"
              :placeholder="t('common.selectStatus')"
              :items="[
                { title: t('common.active'), value: 'true' },
                { title: t('common.inactive'), value: 'false' },
              ]"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('dnc.searchDncGroups')"
            />
          </div>

          <!-- 👉 Add DNC Group button -->
          <VBtn
            prepend-icon="tabler-plus"
            @click="openCreateDialog"
          >
            {{ t('dnc.addDncGroup') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="dncGroups"
        :items-length="totalItems"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- Group Name Column -->
        <template #item.name="{ item }">
          <div class="d-flex align-center">
            <VIcon
              :icon="(item as any).isActive ? 'tabler-folder' : 'tabler-folder-off'"
              :color="(item as any).isActive ? 'success' : 'error'"
              size="20"
              class="me-2"
            />
            <span class="font-weight-medium">{{ (item as any).name }}</span>
          </div>
        </template>

        <!-- Description Column -->
        <template #item.description="{ item }">
          <span
            class="text-truncate d-inline-block"
            style="max-width: 200px;"
          >
            {{ (item as any).description || '-' }}
          </span>
        </template>

        <!-- DNC Lists Count Column -->
        <template #item.dncListsCount="{ item }">
          <VChip
            :color="(item as any).dncListsCount > 0 ? 'primary' : 'default'"
            variant="tonal"
            size="small"
          >
            {{ (item as any).dncListsCount || 0 }}
          </VChip>
        </template>

        <!-- Active Status Column -->
        <template #item.isActive="{ item }">
          <VChip
            :color="(item as any).isActive ? 'success' : 'error'"
            variant="tonal"
            size="small"
          >
            {{ (item as any).isActive ? t('dnc.isActive') : t('dnc.inactive') }}
          </VChip>
        </template>

        <!-- Created Date Column -->
        <template #item.createdAt="{ item }">
          {{ formatDate24h((item as any).createdAt) }}
        </template>

        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-2">
            <VBtn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="openEditDialog(item)"
            >
              <VIcon icon="tabler-edit" />
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ t('dnc.edit') }}
              </VTooltip>
            </VBtn>

            <VBtn
              icon
              size="small"
              variant="text"
              :color="(item as any).isActive ? 'warning' : 'success'"
              @click="toggleActiveStatus(item)"
            >
              <VIcon :icon="(item as any).isActive ? 'tabler-eye-off' : 'tabler-eye'" />
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ (item as any).isActive ? t('dnc.deactivate') : t('dnc.activate') }}
              </VTooltip>
            </VBtn>
          </div>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalItems"
          />
        </template>
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>

    <!-- DNC Group Dialog -->
    <DncGroupDialog
      v-model="showDialog"
      :mode="dialogMode"
      :group="selectedGroup"
      @close="handleDialogClose"
      @dnc-list-updated="refreshDncGroups"
    />
  </section>
</template>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
